import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMediaQuery } from "@/hooks/use-media-query";

import { useStableRest } from "../../utils/use-stable-rest";
import { primeIcon } from "../icon";

import {
  buildRows,
  completeFilterValue,
  hasFilterCriteria,
  isRowComplete,
  pruneFilterValue,
  serializeFilterValue,
} from "./filter-panel.model";
import { COLLAPSE_LABEL, EXPAND_LABEL, UNDO_WINDOW_MS } from "./messages";
import type {
  FilterPanelRootAttributes,
  FilterPanelViewModel,
  FilterRow,
  FilterValue,
  UseFilterPanelOptions,
} from "./types-filter-panel";

const MOBILE_QUERY = "(max-width: 48rem)"; // `--ds-breakpoint-md`

const COLLAPSE_ICON = primeIcon("chevron-up", 14);
const EXPAND_ICON = primeIcon("chevron-down", 14);

/** O que Limpar tirou do painel: linhas, rascunho e critérios aplicados, para o Desfazer devolver. */
interface ClearSnapshot {
  rows: FilterRow[];
  draft: FilterValue;
  value: FilterValue;
}

/** ViewModel do FilterPanel: linhas, rascunho, validação de linha incompleta e Aplicar/Limpar; sem JSX. */
export function useFilterPanel({
  definitions,
  value,
  onChange,
  onApply,
  testId,
  collapsible = true,
  showClear = true,
  className,
  style,
  classNames,
  ...props
}: UseFilterPanelOptions & FilterPanelRootAttributes): FilterPanelViewModel {
  const rest = useStableRest(props);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [rows, setRows] = useState<FilterRow[]>(() => buildRows(definitions, value));
  const [draft, setDraft] = useState<FilterValue>(value);
  const [collapsed, setCollapsed] = useState(false);
  // Só depois de uma tentativa de Aplicar as linhas incompletas mostram o erro.
  const [showErrors, setShowErrors] = useState(false);
  const nextId = useRef(rows.length);
  // Preenchido só durante os 5 s depois de Limpar: o botão vira "Desfazer".
  const [undoSnapshot, setUndoSnapshot] = useState<ClearSnapshot | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const dismissUndo = useCallback(() => {
    clearTimeout(undoTimer.current);
    setUndoSnapshot(null);
  }, []);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  const valueKey = serializeFilterValue(value);
  // Guarda o último conjunto emitido daqui: o que não bate com ele veio de fora e reconstrói o rascunho.
  const emittedKey = useRef(valueKey);

  useEffect(() => {
    if (emittedKey.current === valueKey) return;
    emittedKey.current = valueKey;
    setDraft(value);
    setRows(buildRows(definitions, value));
    setShowErrors(false);
    dismissUndo();
    // `definitions` só muda quando as opções carregam, o que não redesenha as linhas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const definitionByKey = useMemo(
    () => new Map(definitions.map((definition) => [definition.key, definition])),
    [definitions],
  );

  return useMemo(() => {
    const emit = (next: FilterValue) => {
      emittedKey.current = serializeFilterValue(next);
      onChange(next);
    };

    const isIncomplete = (row: FilterRow) => {
      const definition = row.field ? definitionByKey.get(row.field) : undefined;
      return Boolean(definition && row.field && !isRowComplete(definition, draft[row.field] ?? []));
    };

    const hasIncomplete = rows.some(isIncomplete);

    const activeCount = definitions.filter(({ key }) => (value[key] ?? []).length > 0).length;
    const hasCriteria = hasFilterCriteria(draft) || hasFilterCriteria(value);

    return {
      isMobile,
      collapsible,
      showClear,
      className,
      style,
      classNames: classNames ?? {},
      rest,
      definitionByKey,
      rows,
      draft,
      collapsed,
      showErrors,
      activeCount,
      showCount: activeCount > 0,
      collapseIcon: collapsed ? EXPAND_ICON : COLLAPSE_ICON,
      collapseLabel: collapsed ? EXPAND_LABEL : COLLAPSE_LABEL,
      canAddRow: rows.length < definitions.length,
      // Depois de uma tentativa com linha incompleta, Aplicar só volta com a linha completa ou removida.
      canApply: serializeFilterValue(draft) !== valueKey && !(showErrors && hasIncomplete),
      hasCriteria,
      canClear: hasCriteria,
      canUndo: undoSnapshot !== null,
      isIncomplete,
      partTestId: (part: string) => `${testId}-${part}`,
      toggleCollapsed: () => setCollapsed((current) => !current),
      availableDefinitions: (row: FilterRow) => {
        const taken = new Set(
          rows.filter((other) => other.id !== row.id && other.field).map((other) => other.field),
        );
        return definitions.filter((definition) => !taken.has(definition.key));
      },
      addRow: () => {
        dismissUndo();
        setRows((current) => [...current, { id: nextId.current++, field: null }]);
      },
      removeRow: (row: FilterRow) => {
        dismissUndo();
        setRows((current) => {
          const remaining = current.filter((other) => other.id !== row.id);
          return remaining.length > 0 ? remaining : [{ id: nextId.current++, field: null }];
        });
        if (row.field) {
          const removed = row.field;
          setDraft((current) => ({ ...current, [removed]: [] }));
        }
      },
      chooseField: (row: FilterRow, field: string | null) => {
        dismissUndo();
        setRows((current) =>
          current.map((other) => (other.id === row.id ? { ...other, field } : other)),
        );
        if (row.field && (draft[row.field] ?? []).length > 0) {
          const previous = row.field;
          setDraft((current) => ({ ...current, [previous]: [] }));
        }
      },
      fillValues: (field: string, values: string[]) => {
        dismissUndo();
        setDraft((current) => ({ ...current, [field]: values }));
      },
      apply: () => {
        dismissUndo();
        if (hasIncomplete) {
          setShowErrors(true);
          return;
        }
        setShowErrors(false);
        emit(completeFilterValue(definitions, rows, draft));
        onApply?.();
      },
      clear: () => {
        clearTimeout(undoTimer.current);
        setUndoSnapshot({ rows, draft, value });
        undoTimer.current = setTimeout(() => setUndoSnapshot(null), UNDO_WINDOW_MS);
        setDraft({});
        setRows([{ id: nextId.current++, field: null }]);
        setShowErrors(false);
        if (hasFilterCriteria(value)) emit({});
      },
      undo: () => {
        if (!undoSnapshot) return;
        dismissUndo();
        setRows(undoSnapshot.rows);
        setDraft(undoSnapshot.draft);
        if (hasFilterCriteria(undoSnapshot.value)) emit(undoSnapshot.value);
      },
      load: (saved: FilterValue) => {
        dismissUndo();
        const next: FilterValue = Object.fromEntries(
          definitions.map(({ key }) => [key, saved[key] ?? []]),
        );
        setDraft(next);
        setRows(buildRows(definitions, next));
        setShowErrors(false);
      },
      valueToSave: () => pruneFilterValue(hasFilterCriteria(draft) ? draft : value),
    };
  }, [
    isMobile,
    testId,
    collapsible,
    showClear,
    className,
    style,
    classNames,
    rest,
    definitions,
    definitionByKey,
    rows,
    draft,
    collapsed,
    showErrors,
    value,
    valueKey,
    onChange,
    onApply,
    undoSnapshot,
    dismissUndo,
  ]);
}
